import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography, Card, Container, alpha, } from '@mui/material';
import { CalendarToday as CalendarTodayIcon, Event as EventIcon, Group as GroupIcon } from "@mui/icons-material";
import { getData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import Loader from "../../common/spinner";
import PushNotificationManager from "../../services/PushNotificationManager";

function HrDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({});
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const orgId = userData?.organization_id;
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            if (!orgId) return;
            setLoading(true);
            try {
                const url = `${API_ENDPOINTS.getHrDashboardCounts}?userId=${userId}`;
                const res = await getData(url);
                if (res.status === "SUCCESS") {
                    setStats(res.message?.data || {});
                }
            } catch (e) {
                return null;
            }
            finally {
                setLoading(false); // Stop loading regardless of success/fail
            }
        };
        fetchStats();
    }, [orgId, userId]);

    const cards = [
        { id: 1, icon: <CalendarTodayIcon />, title: "Total Employees", count: stats.totalActiveEmployees, p: "#4158D0", s: "#C850C0", path: "/hr/employee-management" },
        { id: 3, icon: <EventIcon />, title: "Attendance Overview", count: `${stats.attendanceToday?.percentage || "0"}%`, p: "#8EC5FC", s: "#E0C3FC", path: "/hr/attendance-management" },
        { id: 4, icon: <GroupIcon />, title: "Leave Requests", count: stats.totalLeaveRequests, p: "#FF3CAC", s: "#784BA0", path: "/hr/leave-management" }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Loader />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <PushNotificationManager userId={userId} />
            <Container maxWidth="xl" sx={{ padding: '0 !important' }}>
                <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                    {cards.map(c => (
                        <Grid key={c.id} size={{ xs: 2, sm: 4, md: 4 }}>
                            <Card
                                onClick={() => c.path && navigate(c.path)}
                                sx={{
                                    borderRadius: '24px',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    px: 3,
                                    py: 3,
                                    background: '#fff',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        background: `linear-gradient(135deg, ${alpha(c.p, 0.08)}, ${alpha(c.s, 0.08)})`,
                                        clipPath: 'circle(70% at 100% 0%)',
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        boxShadow: `0 20px 40px ${alpha(c.p, 0.15)}`,
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <Box>
                                        <Box sx={{
                                            width: 48, height: 48, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: `linear-gradient(135deg, ${c.p}, ${c.s})`, mb: 1.5
                                        }}>
                                            {React.cloneElement(c.icon, { sx: { fontSize: 24, color: '#fff' } })}
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            {c.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: c.p }}>
                                        {c.count ?? 0}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container >
        </Box>
    );
}

export default HrDashboard;